import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";

/*
 * ProCharted's support desk (owner 2026-10-25): backend `api/support`. The Support page is only for the
 * people the server lists in SUPPORT_DESK_EMAILS (the owner); `contact` = "Talk to a person" in the help chat.
 */

/** Help desk v2 (owner 2026-10-25): the list's tabs. */
export type SupportView =
  | "needs_you"
  | "waiting"
  | "solved"
  | "closed"
  | "all"
  | "other"
  /** Cases with an email waiting to go out (Schedule send, owner 2026-11-03). */
  | "scheduled";
/** A case's status: new (just arrived) · open · waiting (on the customer) · solved · closed · other ("Other mail"). */
export type SupportStatus =
  | "new"
  | "open"
  | "waiting"
  | "solved"
  | "closed"
  | "other";
export type SupportPriority = "urgent" | "high" | "normal" | "low";
export type SupportSource =
  | "email"
  | "chat"
  | "bug_report"
  | "website"
  /** Drafts ProCharted made for the owner to review (owner 2026-10-26). */
  | "onboarding"
  | "demo"
  /** A new email the owner wrote from the Help desk ("New email", owner 2026-11-03). */
  | "outbound";
export type SupportCategory =
  | "how_to"
  | "cancellation"
  | "billing"
  | "privacy"
  | "bug"
  | "other";

export type SupportTicketRow = {
  id: string;
  code: string;
  subject?: string | null;
  contactEmail: string;
  contactName?: string | null;
  firmName?: string | null;
  firmPeople?: number | null;
  source: SupportSource;
  category: SupportCategory;
  ownerOnly: boolean;
  status: SupportStatus;
  /** The owner's own status inside the main one (Mg-229), with the name + color to show. */
  customStatusId?: string | null;
  statusName?: string;
  statusColor?: SupportStatusColor;
  unread: boolean;
  hasDraft: boolean;
  preview: string;
  lastMessageAt: string;
  priority: SupportPriority;
  notSure: boolean;
  /** user (a ProCharted user's email) | stripe (a customer in Stripe) */
  knownBy?: "user" | "stripe" | null;
  lastInboundAt?: string | null;
  rating?: "good" | "bad" | null;
  /** Schedule send (Mg-233): when the case's scheduled email goes out. */
  scheduledFor?: string | null;
  scheduledStatus?: SupportScheduledStatus | null;
};

/** pending = waiting to go out · sending = going out right now · failed = couldn't be sent (the owner acts). */
export type SupportScheduledStatus = "pending" | "sending" | "failed";

export type SupportScheduled = {
  id: string;
  sendAt: string;
  timeZone?: string | null;
  status: SupportScheduledStatus;
  text: string;
  then?: "waiting" | "solved" | null;
  signature?: string | null;
  attempts: number;
  error?: string | null;
  createdByEmail?: string | null;
  cc?: string[];
  bcc?: string[];
};

export type SupportNewEmailRequest = SupportCopies & {
  to: string[];
  subject: string;
  text: string;
  signature?: string;
  /** Empty = send now. ISO with offset. */
  sendAt?: string;
  timeZone?: string;
};

export type SupportNewEmailResult = {
  results: {
    email: string;
    ticketId: string;
    code: string;
    state: "sent" | "scheduled" | "failed";
    error?: string | null;
  }[];
  sendAt?: string | null;
};

export type SupportTicketList = {
  tickets: SupportTicketRow[];
  needsYou: number;
  waiting: number;
  solved: number;
  closed: number;
  other: number;
  /** Cases with an email waiting to go out (older servers: missing). */
  scheduled?: number;
  done: number;
  more: boolean;
};

export type SupportTicketQuery = {
  view: SupportView;
  q?: string;
  priority?: SupportPriority | "";
  category?: string;
};

export type SupportMessage = {
  id: string;
  /** note = a private note (never sent) */
  direction: "in" | "out" | "note";
  automatic?: boolean;
  fromEmail?: string | null;
  fromName?: string | null;
  body: string;
  sentByEmail?: string | null;
  at: string;
  /** Who else got this sent email (Cc / Bcc, comma separated). */
  cc?: string | null;
  bcc?: string | null;
};

/** Copies of an email (owner 2026-11-03: Cc and Bcc). */
export type SupportCopies = { cc?: string[]; bcc?: string[] };

export type SupportFirmSteps = {
  clients: boolean;
  work: boolean;
  team: boolean;
  card: boolean;
};

export type SupportTicketDetail = SupportTicketRow & {
  firstReplyAt?: string | null;
  solvedAt?: string | null;
  ackSentAt?: string | null;
  aiSummary?: string | null;
  draft?: string | null;
  draftSources: { title: string; url: string }[];
  draftAt?: string | null;
  messages: SupportMessage[];
  customer: {
    isUser: boolean;
    firmName?: string | null;
    role?: string | null;
    people?: number | null;
    plan?: string | null;
    planStatus?: string | null;
    customerSince?: string | null;
    inStripe?: boolean;
    onboarding?: {
      trialDay?: number | null;
      trialEndsAt?: string | null;
      steps: SupportFirmSteps;
      welcomeSentAt?: string | null;
      checkinSentAt?: string | null;
      trialEndingSentAt?: string | null;
    } | null;
  };
  earlier: {
    id: string;
    subject?: string | null;
    status: SupportStatus;
    at: string;
    rating?: "good" | "bad" | null;
  }[];
  /** The scheduled email waiting to go out (Mg-233), or null. */
  scheduled?: SupportScheduled | null;
  /** The Cc / Bcc of a scheduled email that was cancelled: back into the reply box with its text. */
  draftCopies?: SupportCopies | null;
};

export type SupportStats = {
  open: number;
  openUrgent: number;
  solvedThisWeek: number;
  ratedGood: number;
  ratedBad: number;
  avgFirstReplyMinutes?: number | null;
  newFirms: number;
  newFirmsNoClients: number;
};

export type SupportSavedReply = {
  id: string;
  title: string;
  body: string;
  order: number;
};

export type SupportSignature = {
  id: string;
  name: string;
  body: string;
  isDefault: boolean;
  order: number;
};

export type SupportSignatureList = {
  /** false = the server's database update (Mg-230) isn't there yet. */
  available: boolean;
  /** ProCharted's logo, always drawn under every signature. */
  logoUrl: string;
  signatures: SupportSignature[];
};

export type SupportSkipSender = {
  id: string;
  pattern: string;
  createdAt: string;
};

export type AutoEmailKey =
  | "welcome"
  | "checkin"
  | "trial_ending"
  | "demo_followup"
  | "ack"
  | "nudge"
  | "rating";

export type AutoEmailTemplate = {
  key: AutoEmailKey;
  name: string;
  when: string;
  enabled: boolean;
  delayDays: number;
  subject: string;
  body: string;
  tips?: Record<string, string> | null;
  linkUrl?: string | null;
  draftOnly: boolean;
  hasSubject: boolean;
  hasDelay: boolean;
  placeholders: string[];
  changed: boolean;
  updatedAt?: string | null;
};

export type AutoEmailSave = {
  enabled?: boolean;
  delayDays?: number;
  subject?: string;
  body?: string;
  tips?: Record<string, string>;
  linkUrl?: string;
};

export type AutoEmailPreview = {
  to: string;
  subject: string;
  body: string;
  /** The email as it will look (design, signature); servers before 2026-10-29 don't send it. */
  html?: string;
};
/** A case reply exactly as Send would send it (Help desk "Preview", owner 2026-10-29). */
export type SupportEmailPreview = {
  to: string;
  from: string;
  subject: string;
  html: string;
  /** Copies (Cc / Bcc), comma separated; "" = none. */
  cc?: string;
  bcc?: string;
};

export type NewFirmEmail = {
  key: AutoEmailKey;
  /** sent | draft (waiting for the owner's review) | due | skipped | off */
  state: "sent" | "draft" | "due" | "skipped" | "off" | string;
  at?: string | null;
  note?: string | null;
  ticketId?: string | null;
};

export type NewFirmRow = {
  orgId: string;
  firmName: string;
  ownerName?: string | null;
  ownerEmail?: string | null;
  people: number;
  startedAt: string;
  trialEndsAt?: string | null;
  trialDay: number;
  paying: boolean;
  steps: SupportFirmSteps;
  emails: NewFirmEmail[];
  repliedAt?: string | null;
  ticketId?: string | null;
  clientLink?: string | null;
  workLink?: string | null;
  houseError?: string | null;
  lastEmailError?: string | null;
  /** The status of the firm's "Onboarding – …" work item (owner 2026-10-26: one status per firm). */
  workStatus?: FirmWorkStatus | null;
  /** Still setting up (owner 2026-10-28, firms show from sign-up): "account" = no password yet,
   * "firm" = My Firm's Information not filled in yet; none = done. */
  setup?: "account" | "firm" | null;
};

export type FirmWorkStatus = {
  workId: string;
  workTypeId?: string | null;
  statusId?: string | null;
  subStatusId?: string | null;
  label: string;
  /** The main status's colour, "#RRGGBB". */
  color?: string | null;
};

export type WorkStatusChoice = {
  statusId: string;
  subStatusId?: string | null;
  label: string;
  group: string;
  color?: string | null;
};

export type SupportStatusColor =
  | "grey"
  | "blue"
  | "purple"
  | "yellow"
  | "orange"
  | "green"
  | "red";

/** The owner's own case statuses (inside a main group) and New firms stages, gear on the Help desk. */
export type SupportStatusItem = {
  id: string;
  kind: "case";
  name: string;
  group?: SupportStatus | null;
  color: SupportStatusColor;
  order: number;
  inUse: number;
};

export type SupportStatusesView = {
  available: boolean;
  caseStatuses: SupportStatusItem[];
};

export type SupportStatusSave = {
  kind?: "case";
  name?: string;
  group?: SupportStatus;
  color?: SupportStatusColor;
};

/** Demos booked in Calendly (owner 2026-10-26). */
export type DemoState = "booked" | "completed" | "draft" | "sent" | "canceled";

export type DemoRow = {
  id: string;
  name: string;
  email: string;
  company?: string | null;
  demoAt?: string | null;
  bookedAt: string;
  state: DemoState;
  completedAt?: string | null;
  followUpDueAt?: string | null;
  ticketId?: string | null;
  clientLink?: string | null;
  workLink?: string | null;
  problem?: string | null;
};

export type DemosView = {
  available: boolean;
  demos: DemoRow[];
  houseProblem?: string | null;
};

export type CalendlyStatus = {
  available: boolean;
  connected: boolean;
  accountName?: string | null;
  connectedByEmail?: string | null;
  connectedAt?: string | null;
  lastEventAt?: string | null;
  lastError?: string | null;
  demoWord: string;
};

export type NewFirmsView = {
  firms: NewFirmRow[];
  houseFirmName?: string | null;
  houseProblem?: string | null;
  /** Status choices per work type id (the rows' workStatus.workTypeId). */
  workStatusChoices?: Record<string, WorkStatusChoice[]>;
};

export type HouseBackfillView = {
  houseFirmName?: string | null;
  houseProblem?: string | null;
  candidates: number;
  sample: string[];
  added: number;
  started: boolean;
};

export type SupportMailboxStatus = {
  connected: boolean;
  email?: string | null;
  connectedAt?: string | null;
  lastCheckedAt?: string | null;
  lastError?: string | null;
  aiConfigured: boolean;
  emailConfigured: boolean;
};

export type SupportContactRequest = {
  orgId: string;
  message: string;
  chatExcerpt?: string;
  page?: string;
};

export type SupportContactResult = {
  ticketId: string;
  code: string;
  replyTo: string;
};

/* Help desk "Emails" tab (owner 2026-10-26): support@'s Inbox / Sent, any date. */
export type MailAddress = { email: string; name?: string | null };

export type SupportMailRow = {
  id: string;
  threadId?: string | null;
  subject?: string | null;
  from?: MailAddress | null;
  to: MailAddress[];
  snippet: string;
  /** Unix seconds. */
  date: number;
  unread: boolean;
  hasAttachments: boolean;
  /** Sent from support@ itself. */
  fromUs: boolean;
  caseId?: string | null;
  caseCode?: string | null;
};

export type SupportMailList = {
  connected: boolean;
  mailboxEmail?: string | null;
  folder: "inbox" | "sent";
  emails: SupportMailRow[];
  nextPageToken?: string | null;
};

export type SupportMailView = SupportMailRow & {
  cc: MailAddress[];
  replyTo?: MailAddress | null;
  bodyHtml: string;
  attachmentNames: string[];
};

export type SupportCaseFromEmail = {
  ticketId: string;
  code: string;
  created: boolean;
  added: number;
};

export const supportSlice = createApi({
  reducerPath: "supportSlice",
  tagTypes: [
    "SupportTickets",
    "SupportTicket",
    "SupportMailbox",
    "SupportSaved",
    "SupportSkip",
    "SupportAutoEmails",
    "SupportNewFirms",
    "SupportStatuses",
    "SupportDemos",
    "SupportMail",
    "SupportSignatures",
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      headers.set("Authorization", getAuthHeader().Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getSupportAccess: build.query<{ canUse: boolean }, void>({
      query: () => ({ url: "api/support/access" }),
    }),
    getSupportCount: build.query<{ needsYou: number }, void>({
      query: () => ({ url: "api/support/count" }),
      providesTags: ["SupportTickets"],
    }),
    getSupportMailbox: build.query<SupportMailboxStatus, void>({
      query: () => ({ url: "api/support/mailbox" }),
      providesTags: ["SupportMailbox"],
    }),
    getSupportConnectUrl: build.mutation<
      { url: string },
      { provider: "microsoft" | "google"; returnUrl: string }
    >({
      query: (body) => ({
        url: "api/support/mailbox/connect-url",
        method: "POST",
        body,
      }),
    }),
    disconnectSupportMailbox: build.mutation<{ disconnected: boolean }, void>({
      query: () => ({ url: "api/support/mailbox", method: "DELETE" }),
      invalidatesTags: ["SupportMailbox"],
    }),
    checkSupportInbox: build.mutation<{ added: number }, void>({
      query: () => ({ url: "api/support/mailbox/check-now", method: "POST" }),
      invalidatesTags: ["SupportMailbox", "SupportTickets"],
    }),
    getSupportTickets: build.query<SupportTicketList, SupportTicketQuery>({
      query: ({ view, q, priority, category }) => ({
        url: "api/support/tickets",
        params: {
          view,
          ...(q?.trim() ? { q: q.trim() } : {}),
          ...(priority ? { priority } : {}),
          ...(category ? { category } : {}),
        },
      }),
      providesTags: ["SupportTickets"],
    }),
    getSupportStats: build.query<SupportStats, void>({
      query: () => ({ url: "api/support/stats" }),
      providesTags: ["SupportTickets", "SupportNewFirms"],
    }),
    getSupportTicket: build.query<SupportTicketDetail, string>({
      query: (id) => ({ url: `api/support/tickets/${id}` }),
      providesTags: (_r, _e, id) => [{ type: "SupportTicket", id }],
    }),
    saveSupportDraft: build.mutation<
      SupportTicketDetail,
      { id: string; text: string }
    >({
      query: ({ id, text }) => ({
        url: `api/support/tickets/${id}/draft`,
        method: "PUT",
        body: { text },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SupportTicket", id },
        "SupportTickets",
      ],
    }),
    /** New AI draft; `instructions` = "Write with instructions" (what the reply must say, owner 2026-10-27). */
    redraftSupportTicket: build.mutation<
      SupportTicketDetail,
      string | { id: string; instructions?: string }
    >({
      query: (arg) => {
        const { id, instructions } =
          typeof arg === "string" ? { id: arg, instructions: undefined } : arg;
        return {
          url: `api/support/tickets/${id}/redraft`,
          method: "POST",
          ...(instructions ? { body: { instructions } } : {}),
        };
      },
      invalidatesTags: (_r, _e, arg) => [
        { type: "SupportTicket", id: typeof arg === "string" ? arg : arg.id },
        "SupportTickets",
        "SupportMail",
      ],
    }),
    previewSupportReply: build.mutation<
      SupportEmailPreview,
      SupportCopies & {
        id: string;
        text: string;
        then?: "waiting" | "solved";
        signature?: string;
      }
    >({
      query: ({ id, text, then, signature, cc, bcc }) => ({
        url: `api/support/tickets/${id}/preview`,
        method: "POST",
        body: {
          text,
          then: then || "waiting",
          signature: signature || "",
          cc: cc || [],
          bcc: bcc || [],
        },
      }),
    }),
    sendSupportTest: build.mutation<
      SupportEmailPreview,
      {
        id: string;
        text: string;
        then?: "waiting" | "solved";
        signature?: string;
        to: string;
      }
    >({
      query: ({ id, text, then, signature, to }) => ({
        url: `api/support/tickets/${id}/test`,
        method: "POST",
        body: { text, then: then || "waiting", signature: signature || "", to },
      }),
    }),
    sendSupportReply: build.mutation<
      SupportTicketDetail,
      SupportCopies & {
        id: string;
        text: string;
        then?: "waiting" | "solved";
        /** "" / undefined = the default signature, "none" = no signature, else a signature id. */
        signature?: string;
      }
    >({
      query: ({ id, text, then, signature, cc, bcc }) => ({
        url: `api/support/tickets/${id}/send`,
        method: "POST",
        body: {
          text,
          then: then || "waiting",
          signature: signature || "",
          cc: cc || [],
          bcc: bcc || [],
        },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SupportTicket", id },
        "SupportTickets",
        "SupportDemos",
        "SupportNewFirms",
      ],
    }),
    /** "Schedule send" of the reply box (owner 2026-11-03): the text is a snapshot, the box empties. */
    scheduleSupportReply: build.mutation<
      SupportTicketDetail,
      SupportCopies & {
        id: string;
        text: string;
        then?: "waiting" | "solved";
        signature?: string;
        sendAt: string;
        timeZone?: string;
      }
    >({
      query: ({ id, text, then, signature, sendAt, timeZone, cc, bcc }) => ({
        url: `api/support/tickets/${id}/schedule`,
        method: "POST",
        body: {
          cc: cc || [],
          bcc: bcc || [],
          text,
          then: then || "waiting",
          signature: signature || "",
          sendAt,
          timeZone,
        },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SupportTicket", id },
        "SupportTickets",
      ],
    }),
    /** "Edit time" (also gives a "Couldn't send" email new tries). */
    rescheduleSupportEmail: build.mutation<
      SupportTicketDetail,
      { id: string; sendAt: string; timeZone?: string }
    >({
      query: ({ id, sendAt, timeZone }) => ({
        url: `api/support/tickets/${id}/schedule`,
        method: "PUT",
        body: { sendAt, timeZone },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SupportTicket", id },
        "SupportTickets",
      ],
    }),
    /** Cancel: nothing is sent and the text goes back into the reply box. */
    cancelSupportSchedule: build.mutation<SupportTicketDetail, string>({
      query: (id) => ({
        url: `api/support/tickets/${id}/schedule`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "SupportTicket", id },
        "SupportTickets",
      ],
    }),
    sendSupportScheduledNow: build.mutation<SupportTicketDetail, string>({
      query: (id) => ({
        url: `api/support/tickets/${id}/schedule/send-now`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "SupportTicket", id },
        "SupportTickets",
        "SupportMail",
      ],
    }),
    /** "New email": one case per address, sent now or scheduled. */
    sendNewSupportEmail: build.mutation<
      SupportNewEmailResult,
      SupportNewEmailRequest
    >({
      query: (body) => ({
        url: "api/support/emails/new",
        method: "POST",
        body: { ...body, signature: body.signature || "" },
      }),
      invalidatesTags: ["SupportTickets", "SupportMail"],
    }),
    previewNewSupportEmail: build.mutation<
      SupportEmailPreview,
      SupportNewEmailRequest
    >({
      query: (body) => ({
        url: "api/support/emails/new/preview",
        method: "POST",
        body: { ...body, signature: body.signature || "" },
      }),
    }),
    testNewSupportEmail: build.mutation<
      SupportEmailPreview,
      SupportNewEmailRequest & { testTo: string }
    >({
      query: (body) => ({
        url: "api/support/emails/new/test",
        method: "POST",
        body: { ...body, signature: body.signature || "" },
      }),
    }),
    setSupportStatus: build.mutation<
      SupportTicketDetail,
      { id: string; status: SupportStatus; customStatusId?: string | null }
    >({
      query: ({ id, status, customStatusId }) => ({
        url: `api/support/tickets/${id}/status`,
        method: "PUT",
        body: { status, customStatusId: customStatusId || null },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SupportTicket", id },
        "SupportTickets",
        "SupportStatuses",
      ],
    }),
    setSupportPriority: build.mutation<
      SupportTicketDetail,
      { id: string; priority: SupportPriority }
    >({
      query: ({ id, priority }) => ({
        url: `api/support/tickets/${id}/priority`,
        method: "PUT",
        body: { priority },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SupportTicket", id },
        "SupportTickets",
      ],
    }),
    addSupportNote: build.mutation<
      SupportTicketDetail,
      { id: string; text: string }
    >({
      query: ({ id, text }) => ({
        url: `api/support/tickets/${id}/notes`,
        method: "POST",
        body: { text },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "SupportTicket", id }],
    }),
    markNotCustomer: build.mutation<
      SupportTicketDetail,
      { id: string; skip: "none" | "email" | "domain" }
    >({
      query: ({ id, skip }) => ({
        url: `api/support/tickets/${id}/not-customer`,
        method: "POST",
        body: { skip },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "SupportTicket", id },
        "SupportTickets",
        "SupportSkip",
      ],
    }),
    markIsCustomer: build.mutation<SupportTicketDetail, string>({
      query: (id) => ({
        url: `api/support/tickets/${id}/is-customer`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "SupportTicket", id },
        "SupportTickets",
      ],
    }),
    getSavedReplies: build.query<SupportSavedReply[], void>({
      query: () => ({ url: "api/support/saved-replies" }),
      providesTags: ["SupportSaved"],
    }),
    saveSavedReply: build.mutation<
      SupportSavedReply,
      { id?: string; title: string; body: string }
    >({
      query: ({ id, ...body }) => ({
        url: id
          ? `api/support/saved-replies/${id}`
          : "api/support/saved-replies",
        method: id ? "PUT" : "POST",
        body,
      }),
      invalidatesTags: ["SupportSaved"],
    }),
    deleteSavedReply: build.mutation<{ deleted: boolean }, string>({
      query: (id) => ({
        url: `api/support/saved-replies/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SupportSaved"],
    }),
    getSupportSignatures: build.query<SupportSignatureList, void>({
      query: () => ({ url: "api/support/signatures" }),
      providesTags: ["SupportSignatures"],
    }),
    saveSupportSignature: build.mutation<
      SupportSignature,
      { id?: string; name: string; body: string; isDefault?: boolean }
    >({
      query: ({ id, ...body }) => ({
        url: id ? `api/support/signatures/${id}` : "api/support/signatures",
        method: id ? "PUT" : "POST",
        body,
      }),
      invalidatesTags: ["SupportSignatures"],
    }),
    deleteSupportSignature: build.mutation<{ deleted: boolean }, string>({
      query: (id) => ({
        url: `api/support/signatures/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SupportSignatures"],
    }),
    getSkipSenders: build.query<SupportSkipSender[], void>({
      query: () => ({ url: "api/support/skip-senders" }),
      providesTags: ["SupportSkip"],
    }),
    removeSkipSender: build.mutation<{ deleted: boolean }, string>({
      query: (id) => ({
        url: `api/support/skip-senders/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SupportSkip"],
    }),
    getAutoEmails: build.query<AutoEmailTemplate[], void>({
      query: () => ({ url: "api/support/auto-emails" }),
      providesTags: ["SupportAutoEmails"],
    }),
    saveAutoEmail: build.mutation<
      AutoEmailTemplate,
      { key: AutoEmailKey; body: AutoEmailSave }
    >({
      query: ({ key, body }) => ({
        url: `api/support/auto-emails/${key}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["SupportAutoEmails", "SupportNewFirms"],
    }),
    previewAutoEmail: build.mutation<
      AutoEmailPreview,
      { key: AutoEmailKey; body: AutoEmailSave & { orgId?: string } }
    >({
      query: ({ key, body }) => ({
        url: `api/support/auto-emails/${key}/preview`,
        method: "POST",
        body,
      }),
    }),
    testAutoEmail: build.mutation<
      AutoEmailPreview,
      { key: AutoEmailKey; body: AutoEmailSave }
    >({
      query: ({ key, body }) => ({
        url: `api/support/auto-emails/${key}/test`,
        method: "POST",
        body,
      }),
    }),
    getNewFirms: build.query<NewFirmsView, void>({
      query: () => ({ url: "api/support/new-firms" }),
      providesTags: ["SupportNewFirms"],
    }),
    getHouseBackfill: build.query<HouseBackfillView, void>({
      query: () => ({ url: "api/support/house/backfill" }),
      providesTags: ["SupportNewFirms"],
    }),
    startHouseBackfill: build.mutation<HouseBackfillView, void>({
      query: () => ({ url: "api/support/house/backfill", method: "POST" }),
      invalidatesTags: ["SupportNewFirms"],
    }),
    getSupportStatuses: build.query<SupportStatusesView, void>({
      query: () => ({ url: "api/support/statuses" }),
      providesTags: ["SupportStatuses"],
    }),
    addSupportStatus: build.mutation<SupportStatusesView, SupportStatusSave>({
      query: (body) => ({ url: "api/support/statuses", method: "POST", body }),
      invalidatesTags: ["SupportStatuses"],
    }),
    updateSupportStatus: build.mutation<
      SupportStatusesView,
      SupportStatusSave & { id: string }
    >({
      query: ({ id, ...body }) => ({
        url: `api/support/statuses/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        "SupportStatuses",
        "SupportTickets",
        "SupportTicket",
        "SupportNewFirms",
      ],
    }),
    deleteSupportStatus: build.mutation<SupportStatusesView, string>({
      query: (id) => ({ url: `api/support/statuses/${id}`, method: "DELETE" }),
      invalidatesTags: [
        "SupportStatuses",
        "SupportTickets",
        "SupportTicket",
        "SupportNewFirms",
      ],
    }),
    reorderSupportStatuses: build.mutation<
      SupportStatusesView,
      { kind: "case"; ids: string[] }
    >({
      query: (body) => ({
        url: "api/support/statuses/order",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["SupportStatuses", "SupportNewFirms"],
    }),
    setFirmWorkStatus: build.mutation<
      unknown,
      { orgId: string; statusId: string; subStatusId?: string | null }
    >({
      query: ({ orgId, statusId, subStatusId }) => ({
        url: `api/support/new-firms/${orgId}/work-status`,
        method: "PUT",
        body: { statusId, subStatusId: subStatusId || null },
      }),
      invalidatesTags: ["SupportNewFirms"],
    }),
    getDemos: build.query<DemosView, void>({
      query: () => ({ url: "api/support/demos" }),
      providesTags: ["SupportDemos"],
    }),
    getCalendly: build.query<CalendlyStatus, void>({
      query: () => ({ url: "api/support/calendly" }),
      providesTags: ["SupportDemos"],
    }),
    connectCalendly: build.mutation<CalendlyStatus, string>({
      query: (token) => ({
        url: "api/support/calendly/connect",
        method: "POST",
        body: { token },
      }),
      invalidatesTags: ["SupportDemos"],
    }),
    disconnectCalendly: build.mutation<CalendlyStatus, void>({
      query: () => ({ url: "api/support/calendly", method: "DELETE" }),
      invalidatesTags: ["SupportDemos"],
    }),
    getSupportEmails: build.query<
      SupportMailList,
      { folder: "inbox" | "sent"; pageToken?: string | null; q?: string }
    >({
      query: ({ folder, pageToken, q }) => ({
        url: "api/support/emails",
        params: {
          folder,
          ...(pageToken ? { pageToken } : {}),
          ...(q ? { q } : {}),
        },
      }),
      providesTags: ["SupportMail"],
    }),
    getSupportEmail: build.query<SupportMailView, string>({
      query: (id) => ({ url: "api/support/emails/message", params: { id } }),
      providesTags: ["SupportMail"],
    }),
    createCaseFromEmail: build.mutation<SupportCaseFromEmail, string>({
      query: (id) => ({
        url: "api/support/emails/case",
        method: "POST",
        params: { id },
      }),
      invalidatesTags: ["SupportMail", "SupportTickets"],
    }),
    contactSupport: build.mutation<SupportContactResult, SupportContactRequest>(
      {
        query: (body) => ({ url: "api/support/contact", method: "POST", body }),
      },
    ),
  }),
});

export const {
  useGetSupportAccessQuery,
  useGetSupportCountQuery,
  useGetSupportMailboxQuery,
  useGetSupportConnectUrlMutation,
  useDisconnectSupportMailboxMutation,
  useCheckSupportInboxMutation,
  useGetSupportTicketsQuery,
  useGetSupportTicketQuery,
  useSaveSupportDraftMutation,
  useRedraftSupportTicketMutation,
  useSendSupportReplyMutation,
  useSendSupportTestMutation,
  usePreviewSupportReplyMutation,
  useSetSupportStatusMutation,
  useContactSupportMutation,
  useGetSupportStatsQuery,
  useSetSupportPriorityMutation,
  useAddSupportNoteMutation,
  useMarkNotCustomerMutation,
  useMarkIsCustomerMutation,
  useGetSavedRepliesQuery,
  useSaveSavedReplyMutation,
  useDeleteSavedReplyMutation,
  useGetSupportSignaturesQuery,
  useSaveSupportSignatureMutation,
  useDeleteSupportSignatureMutation,
  useGetSkipSendersQuery,
  useRemoveSkipSenderMutation,
  useGetAutoEmailsQuery,
  useSaveAutoEmailMutation,
  usePreviewAutoEmailMutation,
  useTestAutoEmailMutation,
  useGetNewFirmsQuery,
  useGetHouseBackfillQuery,
  useStartHouseBackfillMutation,
  useGetSupportStatusesQuery,
  useAddSupportStatusMutation,
  useUpdateSupportStatusMutation,
  useDeleteSupportStatusMutation,
  useReorderSupportStatusesMutation,
  useSetFirmWorkStatusMutation,
  useGetDemosQuery,
  useGetCalendlyQuery,
  useConnectCalendlyMutation,
  useDisconnectCalendlyMutation,
  useGetSupportEmailsQuery,
  useGetSupportEmailQuery,
  useCreateCaseFromEmailMutation,
  useScheduleSupportReplyMutation,
  useRescheduleSupportEmailMutation,
  useCancelSupportScheduleMutation,
  useSendSupportScheduledNowMutation,
  useSendNewSupportEmailMutation,
  usePreviewNewSupportEmailMutation,
  useTestNewSupportEmailMutation,
} = supportSlice;
